
import Vue, { PropType } from 'vue';
import { mapActions, mapState } from 'vuex';
import BaseButton from '@/components/base/BaseButton.vue';
import RunnitAsset3D from '@/views/Runnits/RunnitAsset3D.vue';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { ASSET_3D_ACCEPT_ATTRIBUTE } from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFormats';
import { uploadAsset3DToRunnit } from '@/utils/runnitUpload';
import { RunnitNodeField, RunnitNodeRunInputValue } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';

export default Vue.extend({
	name: 'Asset3DInputField',
	mixins: [
		RunnitsImageSelectMixin,
	],
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
	},
	data () {
		return {
			ASSET_3D_ACCEPT_ATTRIBUTE,
			IMAGE_GALLERY_DIALOG_NAV,
			isUploading: false,
		};
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
	},
	computed: {
		...mapState([
			'user',
		]),
		selectedAsset (): RunnitNodeRunInputValue | null {
			return (this.value as RunnitNodeRunInputValue) || null;
		},
		selectionStateTriggerId (): string {
			return `asset-3d-main-action-${this.field.uuid}`;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
	},
	watch: {
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				this.commitSingleSelectionStateValue(
					newVal,
					oldVal,
					this.field.uuid,
					this._get(this.selectedAsset, 'nodeRunResult.uuid') || null,
					this._get(this.selectedAsset, 'upload.id') || null,
					() => this.onInput(null),
					(value) => this.onInput(value),
				);
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
		]),
		onInput (asset: RunnitNodeRunInputValue | null): void {
			this.$emit('input', asset);
		},
		clearAsset (): void {
			this.onInput(null);
			this.clearSelectionsFromState(this.selectionStateTriggerId);
		},
		openFileDialog (): void {
			const input = this.$refs.fileInput as HTMLInputElement;
			if (input) input.click();
		},
		openLibrary (imageGalleryDialogInitialNav: ImageGalleryDialogNav): void {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['ASSET_3D']);
		},
		async onFileChange (event: Event): Promise<void> {
			const input = event.target as HTMLInputElement;
			const file = input.files && input.files[0];
			if (!file) return;

			try {
				this.isUploading = true;
				const upload = await uploadAsset3DToRunnit(file, this.user.id);
				this.onInput({ upload });
			} catch (err: any) {
				console.error('ASSET_3D upload failed', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: err && err.message ? err.message : 'Failed to upload 3D asset',
					show: true,
				});
			} finally {
				this.isUploading = false;
				input.value = '';
			}
		},
	},
	components: {
		BaseButton,
		RunnitAsset3D,
	},
});
